import type { IconCategory, IconProps } from '@heidi/design-system/icons';
import { iconManifest, iconsByCategory } from '@heidi/design-system/icons';
import * as BusinessIcons from '@heidi/design-system/icons/generated/business';
import * as FilesIcons from '@heidi/design-system/icons/generated/files-forms-editing';
import { FileStack } from '@heidi/design-system/icons/generated/files-forms-editing';
import * as MedicalIcons from '@heidi/design-system/icons/generated/medical';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { cn } from '@heidi/utils';
import { useState } from 'react';
import { useIntl } from 'react-intl';

/**
 * Curated icon categories for collection icons.
 * These are the most relevant categories for organizing medical documents.
 *
 * If you add a category here, also import its generated barrel above and add
 * it to `COLLECTION_ICON_MAP`. The barrels scope `import *` to a single
 * category so dynamic lookups don't drag the full icon namespace into the
 * authenticated shared bundle.
 */
const COLLECTION_ICON_CATEGORIES: IconCategory[] = [
  'medical',
  'files-forms-editing',
  'business',
];

type CollectionIconComponent = React.ComponentType<IconProps>;

const COLLECTION_ICON_MAP: Record<string, CollectionIconComponent> = {
  ...MedicalIcons,
  ...FilesIcons,
  ...BusinessIcons,
};

interface IconPickerProps {
  /** Currently selected icon component name */
  value: string | null;
  /** Callback when an icon is selected */
  onSelect: (icon: string | null) => void;
  /** Trigger element - typically an avatar button */
  children: React.ReactNode;
}

/**
 * Renders a design system icon by component name.
 * Falls back to FileStack icon if the component name is invalid.
 */
export const CollectionIcon = ({
  icon,
  size = 16,
  className,
}: {
  icon?: string | null;
  size?: number;
  className?: string;
}) => {
  if (!icon) {
    return <FileStack size={size} className={className} />;
  }

  const IconComponent = COLLECTION_ICON_MAP[icon];
  if (!IconComponent) {
    return <FileStack size={size} className={className} />;
  }

  return <IconComponent size={size} className={className} />;
};

/**
 * Icon picker popover for selecting collection icons.
 * Displays a curated grid of icons organized by category.
 */
export const IconPicker = ({ value, onSelect, children }: IconPickerProps) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  const defaultIconTitle = intl.formatMessage({
    defaultMessage: 'Default',
    id: 'xaS/weht7+',
    description:
      'Tooltip on the default collection icon option (stacked files) in the icon picker',
  });

  const handleSelect = (iconName: string) => {
    onSelect(iconName);
    setOpen(false);
  };

  const handleClear = () => {
    onSelect(null);
    setOpen(false);
  };

  // Flatten all icons from selected categories into a single array
  const allIcons = COLLECTION_ICON_CATEGORIES.flatMap((category) => {
    const iconNames = iconsByCategory[category];
    if (!iconNames) return [];

    return iconNames
      .map((iconName) => {
        const metadata = Object.values(iconManifest).find(
          (m) => m.name === iconName && m.category === category
        );
        if (!metadata) return null;

        const IconComponent = COLLECTION_ICON_MAP[metadata.componentName];
        if (!IconComponent) return null;

        return {
          componentName: metadata.componentName,
          IconComponent,
        };
      })
      .filter(Boolean) as {
      componentName: string;
      IconComponent: CollectionIconComponent;
    }[];
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="center">
        <ScrollArea className="h-80">
          <div className="grid grid-cols-5 gap-1 p-3">
            {/* Default icon as first item */}
            <button
              type="button"
              onClick={handleClear}
              className={cn(
                'flex size-12 items-center justify-center rounded-md transition-colors',
                'hover:bg-surface-hover',
                value === null && 'bg-surface-selected ring-1 ring-border-selected'
              )}
              title={defaultIconTitle}
            >
              <FileStack size={24} />
            </button>

            {/* All icons in continuous grid */}
            {allIcons.map(({ componentName, IconComponent }) => {
              const isSelected = value === componentName;

              return (
                <button
                  key={componentName}
                  type="button"
                  onClick={() => handleSelect(componentName)}
                  className={cn(
                    'flex size-12 items-center justify-center rounded-md transition-colors',
                    'hover:bg-surface-hover',
                    isSelected && 'bg-surface-selected ring-1 ring-border-selected'
                  )}
                  title={componentName}
                >
                  <IconComponent size={24} />
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
